/**
 * dark.ts - FMwvTool Default Dark Mode Skin
 * 
 * Complete dark mode theme with all 122 core variables.
 * Based on consolidated variable mapping system.
 * Designed for proper contrast and readability in dark environments.
 */

import { sharedLayout } from './shared-layout';

export const fmwvtoolDefaultDark = {
  id: 'fmwvtool-default-dark',
  name: 'FMwvTool Default (Dark)',
  description: 'Professional dark theme with complete variable coverage',
  mode: 'dark' as const,
  
  variables: {
    // ========================================================================
    // TIER 1: UNIVERSAL VARIABLES (17)
    // ========================================================================
    
    // Interactive States (5)
    '--interactive-hover': 'rgba(255, 255, 255, 0.08)',
    '--interactive-active': 'rgba(255, 255, 255, 0.15)',
    '--interactive-selected': '#1e3a5f',
    '--interactive-disabled': '#2a2a2a',
    '--interactive-focus': '#60a5fa',
    
    // Surface Hierarchy (4)
    '--surface-base': '#1e1e1e',
    '--surface-raised': '#252525',
    '--surface-overlay': '#2a2a2a',
    '--surface-popover': '#374151',
    
    // Text Hierarchy (5)
    '--text-primary': '#e0e0e0',
    '--text-secondary': '#aaaaaa',
    '--text-tertiary': '#666666',
    '--text-disabled': '#4a4a4a',
    '--text-inverse': '#ffffff',
    
    // Borders (3)
    '--border-default': '#3a3a3a',
    '--border-subtle': '#2a2a2a',
    '--border-strong': '#60a5fa',
    
    // ========================================================================
    // TIER 2: COMPONENT PATTERN VARIABLES (54)
    // ========================================================================
    
    // Dropdown Pattern (8)
    '--dropdown-bg': '#2a2a2a',
    '--dropdown-border': '#3a3a3a',
    '--dropdown-shadow': '0 4px 12px rgba(0, 0, 0, 0.5)',
    '--dropdown-item-bg': 'transparent',
    '--dropdown-item-hover': 'rgba(255, 255, 255, 0.08)',
    '--dropdown-item-active': 'rgba(255, 255, 255, 0.15)',
    '--dropdown-item-selected': '#1e3a5f',
    '--dropdown-item-disabled': '#2a2a2a',
    
    // Table Pattern (10)
    '--table-bg': '#1e1e1e',
    '--table-border': '#3a3a3a',
    '--table-header-bg': '#252525',
    '--table-header-text': '#e0e0e0',
    '--table-header-border': '#3a3a3a',
    '--table-row-bg': '#1e1e1e',
    '--table-row-alt': '#232323',
    '--table-row-hover': 'rgba(255, 255, 255, 0.05)',
    '--table-row-selected': '#1e3a5f',
    '--table-cell-border': '#3a3a3a',
    
    // Card Pattern (6)
    '--card-bg': '#252525',
    '--card-border': '#3a3a3a',
    '--card-shadow': '0 2px 8px rgba(0, 0, 0, 0.3)',
    '--card-hover': 'rgba(255, 255, 255, 0.03)',
    '--card-header-bg': '#2a2a2a',
    '--card-header-text': '#e0e0e0',
    
    // Input Pattern (8)
    '--input-bg': '#2a2a2a',
    '--input-bg-focus': '#2f2f2f',
    '--input-bg-disabled': '#252525',
    '--input-text': '#e0e0e0',
    '--input-placeholder': '#666666',
    '--input-border': '#3a3a3a',
    '--input-border-focus': '#60a5fa',
    '--input-border-error': '#f87171',
    
    // Modal Pattern (6)
    '--modal-bg': '#2a2a2a',
    '--modal-overlay': 'rgba(0, 0, 0, 0.75)',
    '--modal-border': '#3a3a3a',
    '--modal-shadow': '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
    '--modal-header-bg': '#252525',
    '--modal-footer-bg': '#252525',
    
    // Button Pattern (8)
    '--button-bg': '#4a9eff',
    '--button-bg-hover': '#6bb0ff',
    '--button-bg-active': '#3b8eef',
    '--button-text': '#ffffff',
    '--button-text-hover': '#ffffff',
    '--button-border': '#4a9eff',
    '--button-disabled-bg': '#2a2a2a',
    '--button-disabled-text': '#666666',
    
    // Navigation Pattern (8)
    '--nav-bg': '#252525',
    '--nav-border': '#3a3a3a',
    '--nav-text': '#e0e0e0',
    '--nav-text-hover': '#60a5fa',
    '--nav-item-bg': 'transparent',
    '--nav-item-hover': 'rgba(255, 255, 255, 0.08)',
    '--nav-item-active': '#1e3a5f',
    '--nav-item-selected': '#1e3a5f',
    
    // ========================================================================
    // TIER 3: SEMANTIC COLOR VARIABLES (15)
    // ========================================================================
    
    // Primary (3)
    '--color-primary': '#4a9eff',
    '--color-primary-hover': '#6bb0ff',
    '--color-primary-light': '#1e3a5f',
    
    // Success (3)
    '--color-success': '#34d399',
    '--color-success-hover': '#4ade80',
    '--color-success-light': '#064e3b',
    
    // Warning (3)
    '--color-warning': '#fbbf24',
    '--color-warning-hover': '#fcd34d',
    '--color-warning-light': '#78350f',
    
    // Error (3)
    '--color-error': '#f87171',
    '--color-error-hover': '#fca5a5',
    '--color-error-light': '#7f1d1d',
    
    // Info (3)
    '--color-info': '#60a5fa',
    '--color-info-hover': '#93c5fd',
    '--color-info-light': '#1e3a8a',
    
    // ========================================================================
    // TIER 4: UTILITY VARIABLES (imported from shared-layout.ts)
    // ========================================================================
    ...sharedLayout,
    
    // Shadows (theme-specific - dark mode)
    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.3)',
    '--shadow-md': '0 4px 6px rgba(0, 0, 0, 0.4)',
    '--shadow-lg': '0 10px 15px rgba(0, 0, 0, 0.5)',
    '--shadow-xl': '0 20px 25px rgba(0, 0, 0, 0.6)',
    '--shadow-inner': 'inset 0 2px 4px rgba(0, 0, 0, 0.3)',
  },
  
  // Plugin-specific configurations (from original skin)
  plugins: {
    outliner: {
      appearance: {
        nodeIndent: 20,
        nodeHeight: 28,
        fontSize: 14,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        showLines: true,
        compactMode: false,
      }
    },
    pdfViewer: {
      appearance: {
        backgroundColor: '#1e1e1e',
        toolbarHeight: 48,
        sidebarWidth: 250,
      }
    },
    picker: {
      appearance: {
        itemHeight: 32,
        fontSize: 14,
        showIcons: true,
      }
    },
    fieldMatcher: {
      appearance: {
        confidenceColors: {
          high: '#34d399',
          medium: '#fbbf24',
          low: '#f87171',
        }
      }
    },
    tagManager: {
      appearance: {
        tagHeight: 24,
        tagPadding: '4px 8px',
        tagBorderRadius: '12px',
      }
    }
  }
};

export default fmwvtoolDefaultDark;
